/**
 * Os arquivos por trás dos registros no painel "Eventos e horários" (E156.F3, ADR-0106 §2.8):
 * os ticks lidos do nome do arquivo com o vínculo e o limite; cada planilha ou registro
 * recebido, lido (com as contagens) ou não (com o motivo); e as linhas sem identificador, que
 * são dúvida para a operação e não entram na linha do tempo. APENAS o que o backend devolveu.
 */
import { Badge, MonoData } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type {
  RegistroNaJornadaData,
  RegistroSemVinculoData,
  TicksDoNomeData,
} from '@/lib/exame_eletronico_jornada';
import { instanteLocalComoConsta, origemDoRegistro } from '@/lib/exame_eletronico_jornada';

const tj = ptBR.exameEletronico.quadro.jornada;
export const LINHA =
  'flex flex-wrap items-center gap-sc-2 border-l-2 border-border pl-sc-3 text-body-sm';

export function Subtitulo({ children }: { children: string }): JSX.Element {
  return (
    <h5 className="font-titulo text-[12px] uppercase tracking-[0.06em] text-fg-muted">
      {children}
    </h5>
  );
}

/** Os ticks do nome do arquivo: o instante lido sem fuso, o inteiro literal, o vínculo e o limite. */
export function TicksDosArquivos({
  ticks,
}: {
  ticks: readonly TicksDoNomeData[];
}): JSX.Element | null {
  if (ticks.length === 0) return null;
  return (
    <section data-testid="quadro-ticks" className="space-y-sc-1">
      <Subtitulo>{tj.ticks.titulo}</Subtitulo>
      <ul className="space-y-sc-1">
        {ticks.map((t, indice) => (
          <li
            key={t.arquivo}
            className={LINHA}
            data-testid={`quadro-ticks-${indice}`}
            data-vinculo={t.vinculo ?? 'nenhum'}
          >
            <MonoData variant="muted">{instanteLocalComoConsta(t.instante_local)}</MonoData>
            <Badge variant="warning">{tj.ticks.semFuso}</Badge>
            <span className="font-medium text-fg">{t.arquivo}</span>
            <MonoData variant="muted">{t.literal}</MonoData>
            {t.vinculo === 'indicio' ? (
              <Badge variant="info">{tj.vinculo.indicio}</Badge>
            ) : (
              <Badge variant="neutral">{tj.ticks.semCaptura}</Badge>
            )}
            <span className="basis-full text-caption text-fg-dim">
              {[t.unidade, t.granularidade, t.truncamento, t.descricao_do_vinculo ?? '']
                .filter((parte) => parte !== '')
                .join('; ')}
            </span>
          </li>
        ))}
      </ul>
      <p className="max-w-prose text-caption text-fg-dim">{tj.ticks.limite}</p>
    </section>
  );
}

function Contagem({ valor, rotulo }: { valor: number; rotulo: string }): JSX.Element {
  return (
    <span>
      <MonoData variant="emphasis">{valor}</MonoData> {rotulo}
    </span>
  );
}

/** Cada planilha ou registro recebido: lido (com as contagens) ou não (com o motivo). */
export function RegistrosRecebidos({
  registros,
}: {
  registros: readonly RegistroNaJornadaData[];
}): JSX.Element {
  return (
    <section data-testid="quadro-registros" className="space-y-sc-1">
      <Subtitulo>{tj.registros.titulo}</Subtitulo>
      {registros.length === 0 ? (
        <p className="text-body-sm text-fg-dim">{tj.registros.nenhum}</p>
      ) : (
        <ul className="space-y-sc-1">
          {registros.map((r, indice) => (
            <li
              key={r.sha256}
              className={LINHA}
              data-testid={`quadro-registro-${indice}`}
              data-lido={r.motivo_sem_leitura ? 'nao' : 'sim'}
            >
              <span className="font-medium text-fg">{r.arquivo}</span>
              <Badge variant="neutral">{r.formato}</Badge>
              {r.motivo_sem_leitura ? (
                <Badge variant="warning">{tj.registros.motivos[r.motivo_sem_leitura]}</Badge>
              ) : (
                <>
                  <Contagem valor={r.eventos_lidos} rotulo={tj.registros.eventosLidos} />
                  <Contagem valor={r.eventos_da_peca} rotulo={tj.registros.desteDocumento} />
                  <Contagem
                    valor={r.eventos_sem_identificador}
                    rotulo={tj.registros.semIdentificador}
                  />
                  <Contagem valor={r.linhas_sem_leitura} rotulo={tj.registros.linhasSemLeitura} />
                </>
              )}
              {r.linhas_com_fuso_invalido ? (
                <Contagem valor={r.linhas_com_fuso_invalido} rotulo={tj.registros.fusoInvalido} />
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** As linhas sem identificador: dúvida para a operação, fora da linha do tempo. */
export function DuvidasParaAOperacao({
  duvidas,
}: {
  duvidas: readonly RegistroSemVinculoData[];
}): JSX.Element | null {
  if (duvidas.length === 0) return null;
  return (
    <section data-testid="quadro-duvidas" className="space-y-sc-1">
      <Subtitulo>{tj.duvidas.titulo}</Subtitulo>
      <ul className="space-y-sc-1">
        {duvidas.map((d, indice) => (
          <li key={indice} className={LINHA} data-testid={`quadro-duvida-${indice}`}>
            <MonoData variant="muted">{d.instante_literal}</MonoData>
            <span className="text-fg">{d.evento_literal}</span>
            <MonoData variant="muted">{origemDoRegistro(d.origem)}</MonoData>
          </li>
        ))}
      </ul>
      <p className="max-w-prose text-caption text-fg-dim">{tj.duvidas.nota}</p>
    </section>
  );
}
